import { Observable } from '../utils/Observable.js';
import { CompositeUnit } from './CompositeUnit.js';

/**
 * Undo-redo service.
 */
class UndoRedoService extends Observable {
    constructor(options = {}) {
        super();
        this.events = ['undone', 'redone'];
        this.bind(this.events, options);
        this.stack = [];
        this.index = 0;
        this.capacity = 100;
    }
    /**
     * Starts the collection of units. Add those with
     * the addCompositeItem method and call commit. Or cancel to forget about it.
     */
    begin() {
        this.composite = new CompositeUnit();
    }
    /**
     * Cancels the collection process of unit started with 'begin'.
     */
    cancel() {
        this.composite = undefined;
    }
    /**
     * Commits a batch of units.
     */
    commit(execute) {
        if (this.composite.units.length > 0) {
            this._restart(this.composite, execute);
        }
        this.composite = undefined;
    }
    /**
     * Adds a unit as part of the begin-commit batch.
     *
     * @param undoUnit
     */
    addCompositeItem(undoUnit) {
        if (this.composite) {
            this.composite.add(undoUnit);
        }
        else {
            this.add(undoUnit);
        }
    }
    /**
     * Standard addition of a unit. See also the batch version; begin-addCompositeUnit-commit methods.
     *
     * @param undoUnit The unit to be added.
     * @param execute If false, the unit will be added but not executed.
     */
    add(undoUnit, execute) {
        this._restart(undoUnit, execute);
    }
    /**
     * Returns the number of undoable unit in the stack.
     *
     * @returns {Number}
     */
    pop() {
        if (this.index > 0) {
            this.stack.pop();
            this.index--;
        }
    }
    count() {
        return this.stack.length;
    }
    /**
     * Rollback of the unit on top of the stack.
     */
    undo() {
        if (this.index > 0) {
            this.index--;
            this.stack[this.index].undo();
            this.trigger('undone');
        }
    }
    /**
     * Redo of the last undone action.
     */
    redo() {
        if (this.stack.length > 0 && this.index < this.stack.length) {
            this.stack[this.index].redo();
            this.index++;
            this.trigger('redone');
        }
    }
    _restart(composite, execute) {
        // throw away anything beyond this point if this is a new branch
        this.stack.splice(this.index, this.stack.length - this.index);
        this.stack.push(composite);
        if (execute !== false) {
            this.redo();
        }
        else {
            this.index++;
        }
        // check the capacity
        if (this.stack.length > this.capacity) {
            this.stack.splice(0, this.stack.length - this.capacity);
            this.index = this.capacity; // points to the end of the stack
        }
    }
    /**
     * Clears the stack.
     */
    clear() {
        this.stack = [];
        this.index = 0;
    }
}

export { UndoRedoService };
